import { useState, useCallback } from 'react';
import api from '../services/api';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';

interface ScanRecord {
  barcode: string;
  order_number: string;
  sidemark?: string;
  item_number: number;
  package_number: number;
  total_packages: number;
  scanned_in_count: number;
  already_scanned: boolean;
  timestamp: number;
}

export default function ScanInPage() {
  const [recentScans, setRecentScans] = useState<ScanRecord[]>([]);
  const [scanError, setScanError] = useState('');
  const [scanSuccess, setScanSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleBarcodeScan = useCallback(async (barcode: string) => {
    if (processing) return;
    setProcessing(true);
    setScanError('');
    setScanSuccess('');

    try {
      const result = await api.scanInPackage(barcode);

      if (result.already_scanned) {
        setScanError(result.message);
        setTimeout(() => setScanError(''), 3000);
      } else {
        setScanSuccess(result.message);
        setTimeout(() => setScanSuccess(''), 3000);

        setRecentScans(prev => [{
          barcode,
          order_number: result.order_number,
          sidemark: result.sidemark,
          item_number: result.package.item_number,
          package_number: result.package.package_number,
          total_packages: result.total_packages,
          scanned_in_count: result.scanned_in_count,
          already_scanned: false,
          timestamp: Date.now(),
        }, ...prev].slice(0, 20));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Scan failed';
      setScanError(msg);
      setTimeout(() => setScanError(''), 4000);
    } finally {
      setProcessing(false);
    }
  }, [processing]);

  const { inputRef, isScanning } = useBarcodeScanner(handleBarcodeScan);

  return (
    <div className="space-y-6">
      {/* Hidden scanner input */}
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
      />

      <div>
        <h1 className="text-2xl font-bold text-white">Scan In Packages</h1>
        <p className="text-gray-400 mt-1">Staging area — scan package barcodes as they arrive from production</p>
      </div>

      {/* Scanner status */}
      <div className={`rounded-xl p-8 text-center border-2 transition-all ${
        scanSuccess ? 'bg-green-900/40 border-green-500' :
        scanError ? 'bg-red-900/40 border-red-500' :
        isScanning ? 'bg-yellow-900/30 border-yellow-500' :
        'bg-gray-800 border-gray-600 border-dashed'
      }`}>
        {scanSuccess ? (
          <>
            <svg className="w-16 h-16 mx-auto text-green-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-green-300 text-lg font-medium">{scanSuccess}</p>
          </>
        ) : scanError ? (
          <>
            <svg className="w-16 h-16 mx-auto text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-red-300 text-lg font-medium">{scanError}</p>
          </>
        ) : isScanning ? (
          <>
            <div className="animate-pulse">
              <svg className="w-16 h-16 mx-auto text-yellow-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </div>
            <p className="text-yellow-300 text-lg font-medium">Scanning...</p>
          </>
        ) : (
          <>
            <svg className="w-16 h-16 mx-auto text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            <p className="text-gray-400 text-lg font-medium">Ready to scan</p>
            <p className="text-gray-500 text-sm mt-1">Scan any package barcode to register it at the staging area</p>
          </>
        )}

        {processing && (
          <div className="mt-3">
            <div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        )}
      </div>

      {/* Recent scans */}
      {recentScans.length > 0 && (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700">
            <h3 className="text-white font-medium">Recent Scans ({recentScans.length})</h3>
          </div>
          <div className="divide-y divide-gray-700">
            {recentScans.map((scan, i) => (
              <div key={`${scan.barcode}-${scan.timestamp}`} className={`px-4 py-3 flex items-center justify-between ${i === 0 ? 'bg-green-900/20' : ''}`}>
                <div>
                  <p className="text-white text-sm font-medium">
                    {scan.order_number}{scan.sidemark ? ` — ${scan.sidemark}` : ''}
                  </p>
                  <p className="text-gray-400 text-xs">
                    Screen #{scan.item_number}, Pkg {scan.package_number} — {scan.barcode}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 text-sm font-medium">
                    {scan.scanned_in_count}/{scan.total_packages} staged
                  </p>
                  <p className="text-gray-500 text-xs">
                    {new Date(scan.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
